import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { Slider } from "@/lib/components/Slider";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  IconDots,
  IconHistory,
  IconTrash,
  IconVolume,
} from "@tabler/icons-react";
import { VoiceoverInfo } from "./VoiceoverInfo";
import { cn, parseHtmlToPlainText } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import WaveformPlayer from "./WaveformPlayer";
import { formatSeconds, SliderRow, useDraftValue } from "../SliderRow";
import { MAX_SILENCE_MS } from "../MusicTab/MusicOptionsDropdown";
import {
  getVoiceOverOverflowHint,
  useVoiceOverOverflowMs,
  getVoiceOverSilenceHint,
} from "./voiceover-overflow";

function VoiceoverWrapper({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-24 w-full shrink-0 items-center border-t border-grey-200 px-6",
        className,
      )}
    >
      {children}
    </div>
  );
}

function VoiceoverBottomBar({ isLoading }: { isLoading: boolean }) {
  const voiceOvers = useEditorStore((state) => state.voiceOvers);
  const currentVoiceOver = useEditorStore((state) =>
    state.service.getCurrentVoiceOver(),
  );

  if (isLoading) {
    return (
      <VoiceoverWrapper>
        <VoiceoverLoading />
      </VoiceoverWrapper>
    );
  }

  if (voiceOvers.length === 0 && !currentVoiceOver) {
    return null;
  }

  return (
    <VoiceoverWrapper>
      {currentVoiceOver ? <VoiceoverSelected /> : <VoiceoverDeselected />}
    </VoiceoverWrapper>
  );
}

function VoiceoverLoading() {
  return (
    <div className="flex w-full items-center justify-center">
      <LoadingSpinner className="mr-2" />
      <span className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-sm font-medium text-transparent">
        Generating voiceover
      </span>
    </div>
  );
}

function VoiceoverSelected() {
  const clearVoiceOver = useEditorStore(
    (state) => state.service.clearVoiceOver,
  );
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const currentVoiceOver = useEditorStore((state) =>
    state.service.getCurrentVoiceOver(),
  );
  const isPreviewPlaying = useEditorStore(
    (state) => state.isPreviewVoiceOverPlaying,
  );
  const setPreviewVoiceOverPlaying = useEditorStore(
    (state) => state.service.setPreviewVoiceOverPlaying,
  );
  const videoIsPlaying = useEditorStore(
    (state) => state.videoPreviewState.isPlaying,
  );

  const volume = useEditorStore((state) =>
    Math.max(
      0,
      Math.min(state.video!.compositionJson.voiceOver?.volume ?? 1, 1),
    ),
  );

  const startSilenceMs = useEditorStore(
    (state) => state.video!.compositionJson.voiceOver?.startSilenceMs ?? 0,
  );
  const startSilenceDraft = useDraftValue(startSilenceMs);

  const [sliderVolume, setSliderVolume] = useState(volume);

  useEffect(() => {
    setSliderVolume(volume);
  }, [volume]);

  const onVolumeChange = useCallback(
    (value: number) => {
      updateVideo((video) => {
        video.compositionJson.voiceOver!.volume = value;
      });
    },
    [updateVideo],
  );

  const onCommitStartSilence = (value: number) => {
    const clamped = Math.max(0, Math.min(value, MAX_SILENCE_MS));
    startSilenceDraft.commit();
    updateVideo((video) => {
      video.compositionJson.voiceOver!.startSilenceMs = clamped;
    });
  };

  useEffect(() => {
    return () => {
      setPreviewVoiceOverPlaying(false);
    };
  }, [setPreviewVoiceOverPlaying]);

  const overflowMs = useVoiceOverOverflowMs(currentVoiceOver?.durationMs);

  if (!currentVoiceOver) {
    return null;
  }

  return (
    <div className="flex w-full flex-col pt-4 pb-6">
      <VoiceoverInfo
        title={parseHtmlToPlainText(currentVoiceOver.script)}
        actor={currentVoiceOver.voiceActorName}
        isCurrentVoiceover={true}
        variant="selected"
      />
      <div className="mt-3 flex items-center justify-center gap-4">
        <div className="flex-1">
          <WaveformPlayer
            url={currentVoiceOver.src}
            durationMs={currentVoiceOver.durationMs}
            durationWarning={
              overflowMs > 0 ? getVoiceOverOverflowHint(overflowMs) : undefined
            }
            isPlaying={isPreviewPlaying}
            onPlay={() => setPreviewVoiceOverPlaying(true)}
            onPause={() => setPreviewVoiceOverPlaying(false)}
            videoIsPlaying={videoIsPlaying}
            className="mt-0"
            volume={volume}
          />
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="outline">
                <IconDots />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-72"
              align="end"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="flex items-center gap-2 py-2.5 focus:bg-transparent"
              >
                <IconVolume size={16} className="shrink-0" />
                <Slider
                  value={sliderVolume}
                  onValueChange={setSliderVolume}
                  onValueCommit={(newValue) => {
                    setSliderVolume(newValue);
                    onVolumeChange(newValue);
                  }}
                  min={0}
                  max={1}
                  step={0.01}
                />
                <span className="w-10 shrink-0 text-right text-xs text-text-secondary">
                  {Math.round(sliderVolume * 100)}%
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <SliderRow
                label="Silence before voiceover"
                info="Sets how long the video plays before the voiceover starts."
                value={startSilenceDraft.value}
                onValueChange={startSilenceDraft.setDraft}
                onValueCommit={onCommitStartSilence}
                min={0}
                max={MAX_SILENCE_MS}
                step={100}
                formatValue={formatSeconds}
                valueWarning={
                  overflowMs > 0 && startSilenceMs > 0
                    ? getVoiceOverSilenceHint(overflowMs)
                    : undefined
                }
              />
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={clearVoiceOver}>
                <IconTrash />
                <span>Remove voiceover</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <GoToListButton />
        </div>
      </div>
    </div>
  );
}

function VoiceoverDeselected() {
  return (
    <div className="flex w-full items-center justify-between">
      <span className="text-text-secondary">No voiceover selected</span>
      <GoToListButton />
    </div>
  );
}

function GoToListButton() {
  const updateVoiceoverTabState = useEditorStore(
    (state) => state.service.updateVoiceoverTabState,
  );
  return (
    <Tooltip>
      <TooltipTrigger onFocus={(e) => e.stopPropagation()} asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            updateVoiceoverTabState((state) => {
              state.view = "LIST";
            });
          }}
        >
          <IconHistory />
        </Button>
      </TooltipTrigger>
      <TooltipContent>View earlier voiceovers</TooltipContent>
    </Tooltip>
  );
}

export function VoiceoverPreview() {
  const currentVoiceOver = useEditorStore((state) =>
    state.service.getCurrentVoiceOver(),
  );
  const previewVoiceOver = useEditorStore((state) => state.previewVoiceOver);
  const isPreviewPlaying = useEditorStore(
    (state) => state.isPreviewVoiceOverPlaying,
  );
  const setPreviewVoiceOverPlaying = useEditorStore(
    (state) => state.service.setPreviewVoiceOverPlaying,
  );
  const videoIsPlaying = useEditorStore(
    (state) => state.videoPreviewState.isPlaying,
  );

  useEffect(() => {
    return () => {
      setPreviewVoiceOverPlaying(false);
    };
  }, [setPreviewVoiceOverPlaying]);

  const overflowMs = useVoiceOverOverflowMs(previewVoiceOver?.durationMs);

  if (!previewVoiceOver) {
    return null;
  }

  const isCurrentVoiceover =
    previewVoiceOver.externalId === currentVoiceOver?.externalId;

  return (
    <VoiceoverWrapper className="z-50">
      <div className="flex w-full flex-col pt-4 pb-6">
        <VoiceoverInfo
          title={parseHtmlToPlainText(previewVoiceOver.script)}
          actor={previewVoiceOver.voiceActorName}
          variant="selected"
          isCurrentVoiceover={isCurrentVoiceover}
        />
        <WaveformPlayer
          url={previewVoiceOver.src}
          durationMs={previewVoiceOver.durationMs}
          durationWarning={
            overflowMs > 0 ? getVoiceOverOverflowHint(overflowMs) : undefined
          }
          isPlaying={isPreviewPlaying}
          onPlay={() => setPreviewVoiceOverPlaying(true)}
          onPause={() => setPreviewVoiceOverPlaying(false)}
          videoIsPlaying={videoIsPlaying}
        />
      </div>
    </VoiceoverWrapper>
  );
}

export default VoiceoverBottomBar;
